import React, { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../context/AuthContext';
import { FaCommentAlt, FaStar, FaTimes, FaSpinner, FaCheckCircle } from 'react-icons/fa';
import { toast } from 'sonner';

const FeedbackWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [rating, setRating] = useState(0);
    const [message, setMessage] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const { user } = useAuth();
    const location = useLocation();

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!rating) return;

        if (!user) {
            toast.error("Please login to submit feedback.");
            return;
        }

        setIsSubmitting(true);
        try {
            const { error } = await supabase
                .from('feedback')
                .insert([
                    {
                        user_id: user?.id,
                        rating,
                        message,
                        context: location.pathname,
                    },
                ]);

            if (error) throw error;

            // Track feedback submission
            import('posthog-js').then(({ default: posthog }) => {
                posthog.capture('feedback_submitted', {
                    rating,
                    context: location.pathname,
                    has_message: !!message
                });
            });

            setSubmitted(true);
            setTimeout(() => {
                setIsOpen(false);
                setSubmitted(false);
                setRating(0);
                setMessage('');
            }, 2000);
        } catch (error) {
            console.error('Error submitting feedback:', error);
            toast.error('Failed to submit feedback. Please try again.');
        } finally {
            setIsSubmitting(false);
        }
    };

    // if (!user) return null; // Showing for all users now per request

    return (
        <div className="fixed bottom-6 right-6 z-[9999] flex flex-col items-end pointer-events-none">
            {/* Modal */}
            {isOpen && (
                <div className="mb-4 bg-white dark:bg-[#1e293b] rounded-2xl shadow-2xl border border-gray-200 dark:border-gray-700 w-80 overflow-hidden pointer-events-auto animate-scaleIn origin-bottom-right">
                    <div className="p-4 bg-gradient-to-r from-blue-600 to-indigo-600 flex justify-between items-center text-white">
                        <h3 className="font-bold text-sm">Your Feedback</h3>
                        <button onClick={() => setIsOpen(false)} className="hover:bg-white/20 p-1 rounded-full text-white/80 hover:text-white transition-colors">
                            <FaTimes size={12} />
                        </button>
                    </div>

                    <div className="p-5">
                        {submitted ? (
                            <div className="text-center py-6 text-emerald-500 animate-fadeIn">
                                <FaCheckCircle className="mx-auto text-4xl mb-3" />
                                <p className="font-bold text-gray-800 dark:text-gray-200">Thank You!</p>
                                <p className="text-xs text-gray-500 mt-1">We appreciate your input.</p>
                            </div>
                        ) : (
                            <form onSubmit={handleSubmit} className="space-y-4">
                                {/* Rating */}
                                <div className="flex justify-center gap-2">
                                    {[1, 2, 3, 4, 5].map((star) => (
                                        <button
                                            key={star}
                                            type="button"
                                            onClick={() => setRating(star)}
                                            onMouseEnter={() => !rating && setRating(star)} // optional hover effect
                                            className={`text-2xl transition-transform hover:scale-110 ${rating >= star ? 'text-yellow-400' : 'text-gray-300 dark:text-gray-600'
                                                }`}
                                        >
                                            <FaStar />
                                        </button>
                                    ))}
                                </div>

                                <p className="text-center text-xs font-medium text-gray-500 dark:text-gray-400">
                                    {rating === 0 ? "Rate your experience" :
                                        rating === 5 ? "Excellent!" :
                                            rating === 4 ? "Good" :
                                                rating === 3 ? "Okay" :
                                                    rating === 2 ? "Poor" : "Terrible"}
                                </p>

                                <textarea
                                    value={message}
                                    onChange={(e) => setMessage(e.target.value)}
                                    placeholder="Tell us what you think..."
                                    className="w-full p-3 rounded-lg bg-gray-50 dark:bg-[#0f172a] border border-gray-200 dark:border-gray-700 text-sm focus:ring-2 focus:ring-blue-500 outline-none text-gray-700 dark:text-gray-200 resize-none h-24"
                                />

                                <button
                                    type="submit"
                                    disabled={!rating || isSubmitting}
                                    className={`w-full py-2.5 rounded-lg font-bold text-sm text-white flex items-center justify-center gap-2 transition-all ${!rating || isSubmitting
                                        ? 'bg-gray-300 dark:bg-gray-700 cursor-not-allowed'
                                        : 'bg-blue-600 hover:bg-blue-500 shadow-lg shadow-blue-500/20'
                                        }`}
                                >
                                    {isSubmitting ? <FaSpinner className="animate-spin" /> : 'Submit Feedback'}
                                </button>
                            </form>
                        )}
                    </div>
                </div>
            )}

            {/* Button */}
            <button
                onClick={() => setIsOpen(!isOpen)}
                className="pointer-events-auto bg-white dark:bg-[#1e293b] text-gray-700 dark:text-gray-200 border border-gray-200 dark:border-gray-700 p-3.5 rounded-full shadow-xl hover:shadow-2xl hover:scale-110 transition-all group flex items-center justify-center"
                title="Give Feedback"
            >
                <div className="relative">
                    <FaCommentAlt className={isOpen ? 'text-blue-500' : 'text-gray-400 group-hover:text-blue-500'} size={20} />
                    <span className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-red-500 rounded-full border-2 border-white dark:border-[#1e293b] animate-pulse"></span>
                </div>
                {!isOpen && (
                    <span className="max-w-0 overflow-hidden group-hover:max-w-xs ml-0 group-hover:ml-2 transition-all duration-300 ease-out font-bold text-sm whitespace-nowrap">
                        Feedback
                    </span>
                )}
            </button>
        </div>
    );
};

export default FeedbackWidget;
